import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Users, Mail, Calendar, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ReferralTypeCell } from './ReferralTypeCell';
import { extractReferralType } from '@/utils/referralHelpers';

interface ReferralsSectionProps {
  submissionId: number;
  inviterEmail?: string;
}

export function ReferralsSection({ submissionId, inviterEmail }: ReferralsSectionProps) {
  const navigate = useNavigate();

  const { data: referrals, isLoading } = useQuery({
    queryKey: ['referrals', submissionId, inviterEmail],
    queryFn: async () => {
      // Si tenemos email del invitador, buscar todos los referidos de todos sus submissions
      if (inviterEmail) {
        console.log('🔍 ReferralsSection - Fetching referrals for inviter email:', inviterEmail);

        // Paso 1: Obtener todos los submission IDs del invitador (incluye referidos que también invitaron)
        const { data: inviterSubmissions, error: inviterError } = await supabase
          .from('jotform_submissions')
          .select('id')
          .eq('email', inviterEmail)
          .eq('is_deleted_in_jotform', false);

        if (inviterError) {
          console.error('❌ ReferralsSection - Error fetching inviter submissions:', inviterError);
          throw inviterError;
        }

        if (!inviterSubmissions || inviterSubmissions.length === 0) {
          console.log('🔍 ReferralsSection - No submissions found for inviter');
          return [];
        }

        const inviterIds = inviterSubmissions.map(s => s.id);
        console.log('🔍 ReferralsSection - Found inviter submission IDs:', inviterIds);

        // Paso 2: Obtener todos los referidos de esos submissions
        const { data, error } = await supabase
          .from('jotform_submissions')
          .select(`
            id,
            jotform_submission_id,
            email,
            jotform_created_at,
            report_url,
            parent_submission_id,
            raw_answers,
            form_mapping:form_mapping_id (
              form_title,
              jotform_form_id
            )
          `)
          .in('parent_submission_id', inviterIds)
          .eq('is_deleted_in_jotform', false)
          .order('jotform_created_at', { ascending: false });

        console.log('🔍 ReferralsSection - Query result:', {
          data,
          error,
          count: data?.length,
          inviterEmail,
          inviterIds
        });

        if (error) {
          console.error('❌ ReferralsSection - Error fetching referrals:', error);
          throw error;
        }

        return data;
      }

      // Comportamiento original: buscar por submissionId
      console.log('🔍 ReferralsSection - Fetching referrals for submission ID:', submissionId);

      const { data, error } = await supabase
        .from('jotform_submissions')
        .select(`
          id,
          jotform_submission_id,
          email,
          jotform_created_at,
          report_url,
          parent_submission_id,
          raw_answers,
          form_mapping:form_mapping_id (
            form_title,
            jotform_form_id
          )
        `)
        .eq('parent_submission_id', submissionId)
        .eq('is_deleted_in_jotform', false)
        .order('jotform_created_at', { ascending: false });

      console.log('🔍 ReferralsSection - Query result:', {
        data,
        error,
        count: data?.length,
        submissionIdUsed: submissionId
      });

      if (error) {
        console.error('❌ ReferralsSection - Error fetching referrals:', error);
        throw error;
      }

      return data;
    },
    enabled: !!submissionId || !!inviterEmail,
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Referidos
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4">
            <div className="animate-pulse">Cargando referidos...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="w-5 h-5" />
          Referidos
          {referrals && referrals.length > 0 && (
            <Badge variant="secondary" className="ml-2">
              {referrals.length}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {referrals && referrals.length > 0 ? (
          <div className="space-y-3">
            {referrals.map((referral) => {
              const referralType = extractReferralType(referral.raw_answers as Record<string, any>);

              return (
                <div
                  key={referral.id}
                  className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 cursor-pointer"
                  onClick={() => {
                    const formId = referral.form_mapping?.jotform_form_id || '';
                    console.log('🔍 Navigating to referral:', {
                      submission_id: referral.jotform_submission_id,
                      formId
                    });
                    navigate(`/admin/submissions/${referral.jotform_submission_id}?formId=${formId}`);
                  }}
                >
                  <div className="flex items-center gap-4 flex-1">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-muted-foreground" />
                        <span className="font-medium">{referral.email || 'Sin email'}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Calendar className="w-4 h-4" />
                        {new Date(referral.jotform_created_at).toLocaleDateString()}
                      </div>
                    </div>

                    {/* Tipo de relación */}
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">Relación:</span>
                      <ReferralTypeCell type={referralType} size="sm" />
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {referral.report_url ? (
                      <Badge variant="secondary" className="bg-green-100 text-green-800">
                        Reporte generado
                      </Badge>
                    ) : (
                      <Badge variant="outline">
                        Sin reporte
                      </Badge>
                    )}
                    <ExternalLink className="w-4 h-4 text-muted-foreground" />
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-6 text-muted-foreground">
            <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No hay referidos para esta respuesta</p>
            <p className="text-sm mt-1">
              Los referidos aparecerán aquí cuando otros usuarios completen el formulario de referidos
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
